import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../lib/api';
const API_URL = API_BASE;
import { FileText, Search, RefreshCw, CheckCircle, AlertCircle, X } from 'lucide-react';

const hdrs = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function BillsAdjustmentScreen({ mode = 'receipt', theme }) {
  const isDark = theme === 'dark';
  const title = mode === 'payment' ? 'Bills Adjustments (Payments)' : 'Bills Adjustments (Receipts)';

  const [bills, setBills] = useState([]);
  const [partyId, setPartyId] = useState('');
  const [settlementACId, setSettlementACId] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [settlementAccounts, setSettlementAccounts] = useState([]);
  const [showGLPopup, setShowGLPopup] = useState(false);
  const [glSearch, setGlSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [adjustments, setAdjustments] = useState({});
  const [docDate, setDocDate] = useState(new Date().toISOString().split('T')[0]);
  const [narration, setNarration] = useState('');

  // Load party accounts
  useEffect(() => {
    const ep = mode === 'payment' ? 'accounts/all?glType=supplier' : 'accounts/all?glType=customer';
    fetch(`${API_URL}/api/vouchers/${ep}`, { headers: hdrs() })
      .then(r => r.json()).then(j => { if (j.success) setAccounts(j.data || []); }).catch(() => {});
    // Load all GL accounts for settlement account lookup
    fetch(`${API_URL}/api/vouchers/accounts/all`, { headers: hdrs() })
      .then(r => r.json()).then(j => { if (j.success) setSettlementAccounts(j.data || []); }).catch(() => {});
  }, [mode]);

  const loadBills = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const url = `${API_URL}/api/vouchers/bills-adjustments?type=${mode}${partyId ? `&partyId=${partyId}` : ''}`;
      const r = await fetch(url, { headers: hdrs() });
      const j = await r.json();
      if (j.success) {
        setBills(j.data || []);
        setAdjustments({});
      } else { setMessage({ type: 'error', text: j.error || 'Failed to load bills' }); }
    } catch (e) { setMessage({ type: 'error', text: e.message }); }
    setLoading(false);
  }, [mode, partyId]);

  const handleSave = async () => {
    const adjList = Object.entries(adjustments)
      .filter(([, v]) => parseFloat(v) > 0)
      .map(([docId, amount]) => ({ docId, amount: parseFloat(amount) }));
    if (!adjList.length) { setMessage({ type: 'error', text: 'Enter adjustment amounts for at least one bill' }); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/vouchers/bills-adjustments`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ partyId, settlementACId: settlementACId || undefined, adjustments: adjList, narration, docDate }),
      });
      const j = await r.json();
      if (j.success) { setMessage({ type: 'success', text: j.data?.message || 'Bills adjusted' }); loadBills(); }
      else { setMessage({ type: 'error', text: j.error || 'Save failed' }); }
    } catch (e) { setMessage({ type: 'error', text: e.message }); }
    setSaving(false);
  };

  const base = isDark ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900';
  const card = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const inp = isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900';
  const thC = isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600';

  return (
    <div className={`min-h-screen p-4 ${base}`} data-testid="bills-adjustment-screen">
      {/* Header */}
      <div className={`border rounded-lg p-4 mb-4 ${card}`}>
        <div className="flex items-center gap-2 mb-4">
          <FileText size={18} className="text-blue-500" />
          <h2 className="font-semibold text-lg">{title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="erp-form-label">Date</label>
            <input type="date" value={docDate} onChange={e => setDocDate(e.target.value)} className={`erp-input w-full ${inp}`} data-testid="bills-adj-date" />
          </div>
          <div>
            <label className="erp-form-label">{mode === 'payment' ? 'Supplier Account' : 'Customer Account'}</label>
            <select value={partyId} onChange={e => setPartyId(e.target.value)} className={`erp-input w-full ${inp}`} data-testid="bills-adj-party">
              <option value="">-- Select Party --</option>
              {accounts.map(a => <option key={a.GLId} value={a.GLId}>{a.GLName}</option>)}
            </select>
          </div>
          <div>
            <label className="erp-form-label">Settlement Account</label>
            <div className="flex gap-1">
              <input
                readOnly value={settlementACId ? (settlementAccounts.find(a => a.GLId === settlementACId)?.GLName || settlementACId) : ''}
                onClick={() => { setGlSearch(''); setShowGLPopup(true); }}
                className={`erp-input flex-1 cursor-pointer ${inp}`}
                placeholder="Click to select GL account"
                data-testid="bills-adj-settlement-ac"
              />
              {settlementACId && (
                <button onClick={() => setSettlementACId('')} className={`erp-btn erp-btn-ghost px-2 ${inp}`} title="Clear settlement account">
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="erp-form-label">Narration</label>
            <input value={narration} onChange={e => setNarration(e.target.value)} className={`erp-input w-full ${inp}`} placeholder="Narration / Remarks" data-testid="bills-adj-narration" />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={loadBills} disabled={loading} className="erp-btn erp-btn-primary flex items-center gap-1" data-testid="bills-adj-search-btn">
            <Search size={14} /> {loading ? 'Loading…' : 'Load Bills'}
          </button>
          <button onClick={() => { setAdjustments({}); setMessage({ type: '', text: '' }); }} className="erp-btn erp-btn-ghost flex items-center gap-1">
            <RefreshCw size={14} /> Clear
          </button>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`flex items-center gap-2 p-3 rounded mb-3 text-sm ${message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-testid="bills-adj-message">
          {message.type === 'error' ? <AlertCircle size={14} /> : <CheckCircle size={14} />} {message.text}
        </div>
      )}

      {/* Bills Grid */}
      {bills.length > 0 && (
        <div className={`border rounded-lg overflow-hidden mb-4 ${card}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className={thC}>
                <th className="text-left p-2 font-medium">Bill Ref</th>
                <th className="text-left p-2 font-medium">Date</th>
                <th className="text-left p-2 font-medium">Party</th>
                <th className="text-right p-2 font-medium">Bill Amount</th>
                <th className="text-right p-2 font-medium">Paid</th>
                <th className="text-right p-2 font-medium">Balance</th>
                <th className="text-right p-2 font-medium">Adjust Amount</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b, i) => (
                <tr key={b.DocId} className={`border-t ${isDark ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-100 hover:bg-gray-50'}`}>
                  <td className="p-2">{b.BillRef || b.DocId}</td>
                  <td className="p-2">{b.DocDateFormatted || b.DocDate}</td>
                  <td className="p-2">{b.PartyName}</td>
                  <td className="p-2 text-right">{parseFloat(b.BillAmount || 0).toFixed(2)}</td>
                  <td className="p-2 text-right">{parseFloat(b.PaidAmount || 0).toFixed(2)}</td>
                  <td className="p-2 text-right font-medium text-orange-600">{parseFloat(b.Balance || 0).toFixed(2)}</td>
                  <td className="p-2 text-right">
                    <input
                      type="number" min="0" step="0.01" max={b.Balance}
                      value={adjustments[b.DocId] || ''}
                      onChange={e => setAdjustments(prev => ({ ...prev, [b.DocId]: e.target.value }))}
                      className={`erp-input w-28 text-right ${inp}`}
                      placeholder="0.00"
                      data-testid={`bills-adj-amount-${i}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={`border-t font-semibold ${thC}`}>
                <td colSpan={5} className="p-2 text-right">Total Adjustment:</td>
                <td className="p-2 text-right text-green-600">
                  {Object.values(adjustments).reduce((s, v) => s + (parseFloat(v) || 0), 0).toFixed(2)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {bills.length === 0 && !loading && (
        <div className={`border rounded-lg p-8 text-center text-sm ${card} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Select a party and click "Load Bills" to view outstanding bills
        </div>
      )}

      {bills.length > 0 && (
        <div className="flex justify-end">
          <button onClick={handleSave} disabled={saving} className="erp-btn erp-btn-primary" data-testid="bills-adj-save-btn">
            {saving ? 'Saving…' : 'Save Adjustments'}
          </button>
        </div>
      )}

      {/* GL Account Lookup Popup */}
      {showGLPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className={`rounded-lg shadow-xl w-96 max-h-96 flex flex-col ${card}`} data-testid="bills-adj-gl-popup">
            <div className={`flex items-center justify-between p-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <span className="font-semibold text-sm">Select Settlement Account</span>
              <button onClick={() => setShowGLPopup(false)} className="erp-btn-icon"><X size={14} /></button>
            </div>
            <div className="p-2">
              <input
                autoFocus
                value={glSearch} onChange={e => setGlSearch(e.target.value)}
                className={`erp-input w-full ${inp}`} placeholder="Search GL accounts…"
                data-testid="bills-adj-gl-search"
              />
            </div>
            <div className="overflow-y-auto flex-1">
              {settlementAccounts
                .filter(a => !glSearch || a.GLName?.toLowerCase().includes(glSearch.toLowerCase()) || a.GLId?.toLowerCase().includes(glSearch.toLowerCase()))
                .slice(0, 100)
                .map(a => (
                  <div
                    key={a.GLId}
                    onClick={() => { setSettlementACId(a.GLId); setShowGLPopup(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer border-b ${isDark ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-blue-50'} ${settlementACId === a.GLId ? (isDark ? 'bg-blue-900/30' : 'bg-blue-100') : ''}`}
                    data-testid={`bills-adj-gl-option-${a.GLId}`}
                  >
                    <span className="font-mono text-xs text-gray-500 mr-2">{a.GLId}</span>
                    {a.GLName}
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
